
import { api } from "@/api/api";
import { ApiCreateCostDto, ApiCostTypeDto, ApiUpdateCostDto } from "@/api/generated/Api";
import BaseModalForm from "@/components/shared/modal/BaseModalForm.vue";
import { ModalType } from "@/shared/enums/modalTypeEnum";
import { NotificationItemType } from "@/shared/enums/notificationItemEnum";
import { globalLoadingWrapper } from "@/shared/helpers/loadingHelpers";
import { openNotification } from "@/shared/helpers/store.helpers";
import { useRouteComputed, useStore } from "@/shared/useHelpers";
import { StoreState } from "@/store/store.state.interface";
import { computed, defineComponent, onMounted, PropType, ref } from "@vue/composition-api";
import Vue from "vue";
import { TeachingMethods } from "@/shared/enums/teaching-methods.enum";
import { ExpenseType } from "@/shared/enums/expenseType.enum";
import { validateNonNegativeNumber } from "@/shared/helpers/validationHelpers";
import { TravelAndExpenseType } from "@/shared/enums/courseRefund.enum";
import { expenseCostSettingControls } from "@/shared/constants/expenseCostSettings";

interface ComponentProps {
  headline: string;
  modalType: ModalType;
  costTypes: ApiCostTypeDto[];
  elementId?: number;
}

const toOptionalCostNumber = (value: unknown): number | null => {
  if (value === "" || value == null) {
    return null;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error("Invalid numeric cost value");
  }
  return value;
};

export default defineComponent({
  name: "ExpensesModal",
  components: { BaseModalForm },
  props: {
    modalType: {
      type: String as PropType<ModalType>,
      required: true,
    },
    headline: {
      type: String,
      required: true,
    },
    costTypes: {
      type: Array,
      required: true,
    },
    elementId: {
      type: Number,
    },
  },
  setup(props: ComponentProps, { emit, refs }) {
    const store = useStore<StoreState>();
    const route = useRouteComputed();
    // the names and IDs collected here are from ApiCostTypeDto[] but is currently hardcoded as the DTO contains
    // categories that should not be included in the dropdowns
    const costTypesExpenses = [{ name: "Andre utgifter", id: 512 }];
    const costTypesSalaryCost = [
      { name: "Undervisningstimer", id: 510 },
      { name: "Andre timer", id: 511 },
    ];

    const travelAndExpenseTypes = Object.values(TravelAndExpenseType);

    const costName = computed(() => route.value.name);
    const isEditing = computed(() => props.modalType === ModalType.Edit);
    const isLoading = ref(props.modalType !== ModalType.Add);
    const isSaving = ref(false);
    const loadError = ref("");
    const costTypeId = ref<number | undefined>(props.costTypes[0]?.id);
    const costModel = ref<ApiUpdateCostDto>({
      name: "",
      description: "",
      price: 0,
      payType: 0,
      articleNo: "",
      teachingMethod: "",
      deductionCode: 0,
      isActive: true,
      travelAndExpenseType: "",
      ...(costName.value === ExpenseType.ExpenseRoute
        ? {
            economySystemSettings: {
              quantity: props.modalType === ModalType.Add,
              rate: props.modalType === ModalType.Add,
              amount: false,
            },
          }
        : {}),
    });

    onMounted(async () => {
      if (props.modalType === ModalType.Add) {
        return;
      }
      try {
        const id = props.elementId;
        if (id === undefined || !Number.isInteger(id) || id <= 0) {
          throw new Error("Invalid cost id");
        }
        const { data: cost } = await api.economy.getCostAsync(id);
        if (cost.id !== id) {
          throw new Error("The requested cost was not returned");
        }
        costTypeId.value = cost.costTypeId;
        costModel.value = {
          name: cost.name,
          description: cost.description,
          price: cost.price,
          payType: cost.payType,
          articleNo: cost.articleNo,
          teachingMethod: cost.teachingMethod,
          deductionCode: cost.deductionCode,
          isActive: cost.isActive,
          travelAndExpenseType: cost.travelAndExpenseType,
          ...(costName.value === ExpenseType.ExpenseRoute
            ? {
                economySystemSettings: {
                  quantity: cost.economySystemSettings?.quantity ?? false,
                  rate: cost.economySystemSettings?.rate ?? false,
                  amount: cost.economySystemSettings?.amount ?? false,
                },
              }
            : {}),
        };
      } catch {
        loadError.value = "Kunne ikke hente kostnaden. Lukk vinduet og prøv igjen.";
      } finally {
        isLoading.value = false;
      }
    });

    const handleSubmit = async () => {
      if (isLoading.value || isSaving.value || loadError.value || props.modalType === ModalType.Display) {
        return;
      }
      const isValid = (refs.connectForm as Vue & { validate: () => boolean }).validate();
      if (!isValid) {
        return;
      }
      isSaving.value = true;
      try {
        await globalLoadingWrapper({ blocking: true }, async () => {
          const updatePayload: ApiUpdateCostDto = {
            ...costModel.value,
            price: toOptionalCostNumber(costModel.value.price),
            payType: toOptionalCostNumber(costModel.value.payType),
            deductionCode: toOptionalCostNumber(costModel.value.deductionCode),
          };
          if (props.modalType === ModalType.Add && costTypeId.value !== undefined) {
            const payload: ApiCreateCostDto = { ...updatePayload, costTypeId: costTypeId.value };
            await api.economy.createCostAsync(payload);
            openNotification(store, NotificationItemType.Success, "Kostnad lagt til");
          } else if (isEditing.value && props.elementId !== undefined) {
            await api.economy.updateCostAsync(props.elementId, updatePayload);
            openNotification(store, NotificationItemType.Success, "Kostnad oppdatert");
          } else {
            throw new Error("Invalid cost form");
          }
          emit("closeAndLoad");
        });
      } catch {
        openNotification(store, NotificationItemType.Error, "Kunne ikke lagre kostnaden. Prøv igjen.");
      } finally {
        isSaving.value = false;
      }
    };
    return {
      costModel,
      costTypeId,
      isEditing,
      isLoading,
      isSaving,
      loadError,
      ExpenseType,
      route,
      costTypesSalaryCost,
      costTypesExpenses,
      costSettingControls: expenseCostSettingControls,
      handleSubmit,
      costName,
      teachingMethodsList: computed(() => Object.values(TeachingMethods)),
      readonly: computed(() => props.modalType === ModalType.Display || isSaving.value),
      travelAndExpenseTypes,
      validateNonNegativeNumber,
    };
  },
});
